import React, { useMemo } from 'react';
import { GoogleLogo, MicrosoftLogo } from './ProviderLogos';
import { ACCOUNT_BASE_PATH } from './pages/accountPaths';

type SocialLoginCardProps = {
  heading?: string;
  description?: string;
  /** After OAuth login, redirect back to this path (e.g. current page). */
  returnPath?: string;
};

export const SocialLoginCard = ({
  heading = 'Sign in',
  description = 'Free to create an account — pick your way to sign in, quick and secure.',
  returnPath,
}: SocialLoginCardProps) => {
  const postAuthPath = returnPath ?? ACCOUNT_BASE_PATH;

  const googleHref = useMemo(() => {
    const params = new URLSearchParams({ pa: postAuthPath });
    return `/api/auth/google/login?${params.toString()}`;
  }, [postAuthPath]);

  const microsoftHref = useMemo(() => {
    const params = new URLSearchParams({ pa: postAuthPath });
    return `/api/auth/microsoft/login?${params.toString()}`;
  }, [postAuthPath]);

  return (
    <aside
      className="rounded-3xl border-[3px] border-ink/15 bg-white/95 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]"
      aria-labelledby="signin-heading"
    >
      <h2 id="signin-heading" className="font-display text-xl font-semibold text-ink">
        {heading}
      </h2>
      <p className="mt-1 font-sans text-sm leading-relaxed text-ink/65">
        {description}
      </p>

      <div className="mt-5 flex flex-col gap-3">
        <a
          href={microsoftHref}
          aria-label="Continue with Microsoft Outlook"
          className="flex w-full items-center justify-center gap-3 rounded-2xl border-2 border-ink/10 bg-[#0078D4] px-4 py-3 font-sans text-sm font-semibold text-white shadow-sm transition hover:brightness-110 active:translate-y-px"
        >
          <MicrosoftLogo className="h-5 w-5" />
          Continue with Microsoft Outlook
        </a>
        <a
          href={googleHref}
          aria-label="Continue with Google Mail"
          className="flex w-full items-center justify-center gap-3 rounded-2xl border-2 border-ink/15 bg-white px-4 py-3 font-sans text-sm font-semibold text-ink shadow-sm transition hover:bg-paper active:translate-y-px"
        >
          <GoogleLogo className="h-5 w-5" />
          Continue with Google Mail
        </a>
      </div>

      <p className="mt-5 font-sans text-xs leading-relaxed text-ink/45">
        By continuing you agree to your school’s sign-in policies. After sign-in you land on your
        account page.
      </p>
    </aside>
  );
};
